import api from '@/services/axiosApi'

export const AnalyticsEventTypes = Object.freeze({
  // User Authentication & Onboarding
  // REGISTERED: 'Completed signup user details',
  LOGGED_IN: 'Logged in',
  LOGGED_OUT: 'Logged out',
  //ACCESSED_TEAM: 'Accessed team',
  //TEAM_SWITCHED: 'Team switched',

  // Creation Events
  CAMPAIGN_CREATED: 'Campaign created',
  EMAIL_CREATED: 'Email created',
  GOAL_CREATED: 'Goal created',
  TAG_CREATED: 'Tag created',
  SEGMENT_GROUP_CREATED: 'Segment group created',
  SEGMENT_CREATED: 'Segment created',
  QUESTION_CREATED: 'Question created',
  RIGHT_ASK_QUESTION_CREATED: 'RightAsk Question created',
  FLOW_CREATED: 'Flow created',
  WIDGET_CREATED: 'Widget created',
  OFFER_CREATED: 'Offer created',
  FORM_CREATED: 'Form created',
  INTEGRATION_CREATED: 'Integration created',
  PHRASE_CREATED: 'Phrase created',
  ATTRIBUTE_CREATED: 'Attribute created',
  TEAM_CREATED: 'Team created',

  // Update Events
  CAMPAIGN_UPDATED: 'Campaign updated',
  GOAL_UPDATED: 'Goal updated',
  SEGMENT_GROUP_UPDATED: 'Segment group updated',
  SEGMENT_UPDATED: 'Segment updated',
  QUESTION_UPDATED: 'Question updated',
  FLOW_UPDATED: 'Flow updated',
  WIDGET_UPDATED: 'Widget updated',
  OFFER_UPDATED: 'Offer updated',
  INTEGRATION_UPDATED: 'Integration updated',
  SETTINGS_UPDATED: 'Settings updated',
  PROFILE_UPDATED: 'Profile updated',
  TEAM_UPDATED: 'Team updated',

  // Delete Events
  CAMPAIGN_DELETED: 'Campaign deleted',
  EMAIL_DELETED: 'Email deleted',
  GOAL_DELETED: 'Goal deleted',
  SEGMENT_GROUP_DELETED: 'Segment group deleted',
  SEGMENT_DELETED: 'Segment deleted',
  QUESTION_DELETED: 'Question deleted',
  FLOW_DELETED: 'Flow deleted',
  WIDGET_DELETED: 'Widget deleted',
  OFFER_DELETED: 'Offer deleted',
  INTEGRATION_DELETED: 'Integration deleted',
  TEAM_DELETED: 'Team deleted',

  // Team Management Events
  TEAM_MEMBER_ADDED: 'Team member added',
  TEAM_MEMBER_REMOVED: 'Team member removed',
  TEAM_MEMBER_UPDATED: 'Team member updated',

  // View/Navigation Events
  VIEWED_DASHBOARD: 'Viewed dashboard',
  VIEWED_FEED: 'Viewed feed',
  VIEWED_LEADS_FEED: 'Viewed leads feed',
  VIEWED_SEGMENT_GROUPS: 'Viewed segment groups',
  VIEWED_SEGMENT_GROUP_DETAILS: 'Viewed segment group details',
  VIEWED_QUESTIONS: 'Viewed questions',
  VIEWED_QUESTION_DETAILS: 'Viewed question details',
  VIEWED_CAMPAIGNS: 'Viewed campaigns',
  VIEWED_CAMPAIGN_DETAILS: 'Viewed campaign details',
  VIEWED_SEGMENTS: 'Viewed segments',
  VIEWED_SEGMENT_DETAILS: 'Viewed segment details',
  VIEWED_FLOWS: 'Viewed flows',
  VIEWED_FLOW_BUILDER: 'Viewed flow builder',
  VIEWED_WIDGETS: 'Viewed widgets',
  VIEWED_WIDGET_DETAILS: 'Viewed widget details',
  VIEWED_OFFERS: 'Viewed offers',
  VIEWED_OFFER_DETAILS: 'Viewed offer details',
  VIEWED_FORMS: 'Viewed forms',
  VIEWED_FORM_DETAILS: 'Viewed form details',
  VIEWED_EMAILS: 'Viewed emails',
  VIEWED_EMAIL_DETAILS: 'Viewed email details',
  VIEWED_GOALS: 'Viewed goals',
  VIEWED_GOAL_DETAILS: 'Viewed goal details',
  VIEWED_INTEGRATIONS: 'Viewed integrations',
  VIEWED_INTEGRATION_DETAILS: 'Viewed integration details',
  VIEWED_INSIGHTS: 'Viewed insights',
  VIEWED_ANALYTICS: 'Viewed analytics',
  VIEWED_SETTINGS: 'Viewed settings',
  VIEWED_TEAM_SETTINGS: 'Viewed team settings',
  VIEWED_PROFILE: 'Viewed profile',
  VIEWED_BILLING: 'Viewed billing',
  VIEWED_ATTRIBUTION: 'Viewed attribution',
  VIEWED_PERFECT_PAGE: 'Viewed perfect page',
  VIEWED_TEMPLATES: 'Viewed templates',
  VIEWED_TEMPLATE_DETAILS: 'Viewed template details',
  VIEWED_HELP: 'Viewed help',

  // Feature Interaction Events
  PUBLISHED: 'Published snippet',
  PREVIEW_STARTED: 'Preview started',
  PREVIEW_ENDED: 'Preview ended',
  TEST_MODE_ENABLED: 'Test mode enabled',
  TEST_MODE_DISABLED: 'Test mode disabled',
  SNIPPET_COPIED: 'Snippet copied',
  SNIPPET_INSTALLED: 'Snippet installed',
  EXPORT_INITIATED: 'Export initiated',
  IMPORT_INITIATED: 'Import initiated',
  BULK_ACTION_PERFORMED: 'Bulk action performed',
  SEARCH_PERFORMED: 'Search performed',
  FILTER_APPLIED: 'Filter applied',
  SORT_CHANGED: 'Sort changed',

  // Engagement & Progress Events
  TUTORIAL_STARTED: 'Tutorial started',
  TUTORIAL_COMPLETED: 'Tutorial completed',
  TOOLTIP_VIEWED: 'Tooltip viewed',
  DOCUMENTATION_ACCESSED: 'Documentation accessed',
  SUPPORT_CONTACTED: 'Support contacted',
  FEEDBACK_PROVIDED: 'Feedback provided',
  MILESTONE_REACHED: 'Milestone reached',
  FIRST_CONVERSION: 'First conversion tracked',

  // AI/Perfect Page Events
  AI_SUGGESTION_GENERATED: 'AI suggestion generated',
  AI_SUGGESTION_APPLIED: 'AI suggestion applied',
  AI_SUGGESTION_REJECTED: 'AI suggestion rejected',
  PERFECT_PAGE_SCAN_INITIATED: 'Perfect page scan initiated',
  PERFECT_PAGE_CHANGES_APPLIED: 'Perfect page changes applied',

  // Error & Recovery Events
  ERROR_ENCOUNTERED: 'Error encountered',
  ERROR_RECOVERED: 'Error recovered',
  VALIDATION_FAILED: 'Validation failed',
  API_ERROR: 'API error',

  // Subscription & Billing Events
  TRIAL_STARTED: 'Trial started',
  TRIAL_EXTENDED: 'Trial extended',
  SUBSCRIPTION_STARTED: 'Subscription started',
  SUBSCRIPTION_UPGRADED: 'Subscription upgraded',
  SUBSCRIPTION_DOWNGRADED: 'Subscription downgraded',
  SUBSCRIPTION_CANCELLED: 'Subscription cancelled',
  PAYMENT_METHOD_ADDED: 'Payment method added',
  PAYMENT_METHOD_UPDATED: 'Payment method updated'
})

export const recordAnalyticsEvent = (event: string, params = {}) => {
  const { user } = useAuthStore()
  const { currentTeamPid, currentTeam } = useConfigStore()

  if (!user) return
  if (
    user.is_admin &&
    !user.owned_teams.find((t) => String(t.pid) === String(currentTeamPid))
  )
    return

  // Try to get route info - might be undefined if called outside component context
  let routeInfo = {}
  try {
    const route = useRoute()
    if (route) {
      routeInfo = {
        currentPath: route.path,
        currentRouteName: route.name,
        currentModule: route.path?.split('/')[1] || 'home',
        queryParams: route.query || {}
      }
    }
  } catch (e) {
    // Route not available in this context
    routeInfo = {
      currentPath: window.location.pathname,
      currentRouteName: null,
      currentModule: window.location.pathname.split('/')[1] || 'home',
      queryParams: {}
    }
  }

  // Automatically enrich event parameters with contextual data
  const enrichedParams = {
    ...params,
    // Team context
    teamPid: currentTeam?.pid,
    teamName: currentTeam?.name,

    // Session context
    sessionId:
      window.sessionStorage.getItem('analytics_session_id') ||
      generateSessionId(),
    timestamp: new Date().toISOString(),

    // Page context
    ...routeInfo

    // Browser context
    // userAgent: navigator.userAgent,
    // screenResolution: `${window.screen.width}x${window.screen.height}`,
    // viewportSize: `${window.innerWidth}x${window.innerHeight}`,

    // Feature usage context
    // hasIntegrations: currentTeam?.integrations?.length > 0,
    // hasPublishedConfig: currentTeam?.last_published_at !== null,
    // daysSinceSignup: user.created_at
    //   ? Math.floor(
    //       (Date.now() - new Date(user.created_at).getTime()) /
    //         (1000 * 60 * 60 * 24)
    //     )
    //   : 0,

    // // Trial context
    // isOnTrial: user.subscription_status === 'trialing',
    // trialDaysRemaining: user.trial_ends_at
    //   ? Math.floor(
    //       (new Date(user.trial_ends_at).getTime() - Date.now()) /
    //         (1000 * 60 * 60 * 24)
    //     )
    //   : null
  }

  return api.post(`/api/${currentTeamPid}/analytics`, {
    event,
    params: enrichedParams
  })
}

// Helper function to generate or retrieve session ID
function generateSessionId(): string {
  const sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  window.sessionStorage.setItem('analytics_session_id', sessionId)
  return sessionId
}
